import React from 'react';
import {
  CheckCircle,
  XCircle,
  Clock,
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Eye,
  FileText,
  Database,
  AlertCircle,
  Users,
  MoreVertical,
  RefreshCw,
  Trash2
} from 'lucide-react';
import UncertaintyPanel from './UncertaintyPanel';
import type { IBVDecision } from './IBVApprovals';

interface DecisionCardProps {
  decision: IBVDecision;
  index: number;
  isNewlyCompleted: boolean;
  selectedReviewDecision: IBVDecision | null;
  reviewUpdating: boolean;
  handleReviewClick: (decision: IBVDecision) => void;
  handleApproveDeclineReview: (approve: boolean) => void;
  setSelectedReviewDecision: (decision: IBVDecision | null) => void;
  formatTimestamp: (timestamp: string) => string;
  formatMoney: (amount: number | null | undefined | string, decimals?: number) => string;
  formatTokens: (tokens: number) => string;

  // Modal/dropdown state
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  activeTab: string | null;
  setActiveTab: (tab: string | null) => void;

  // Modal setters
  setSelectedTransactionDecision: (decision: IBVDecision) => void;
  setTransactionModalOpen: (open: boolean) => void;
  setSelectedReportDecision: (decision: IBVDecision) => void;
  setReportModalOpen: (open: boolean) => void;
  setSelectedUnifiedRawDecision: (decision: IBVDecision) => void;
  setUnifiedRawDataModalOpen: (open: boolean) => void;
  setSelectedAiReasoning: (data: { reasoning: string; decision: IBVDecision }) => void;
  setAiModalOpen: (open: boolean) => void;

  // Actions
  addNotification: (notification: any) => void;
  fetchDecisions: () => void;
}

const DecisionCard: React.FC<DecisionCardProps> = ({
  decision,
  index,
  isNewlyCompleted,
  selectedReviewDecision,
  reviewUpdating,
  handleReviewClick,
  handleApproveDeclineReview,
  setSelectedReviewDecision,
  formatTimestamp,
  formatMoney,
  formatTokens,
  openDropdownId,
  setOpenDropdownId,
  activeTab,
  setActiveTab,
  setSelectedTransactionDecision,
  setTransactionModalOpen,
  setSelectedReportDecision,
  setReportModalOpen,
  setSelectedUnifiedRawDecision,
  setUnifiedRawDataModalOpen,
  setSelectedAiReasoning,
  setAiModalOpen,
  addNotification,
  fetchDecisions
}) => {
  const reasons = decision.reasons || (decision.decision_reasoning ? [decision.decision_reasoning] : []);
  const aiSection = reasons.findIndex((r: string) => r.includes('🤖 AI Analysis'));
  const baseReasons = aiSection >= 0 ? reasons.slice(0, aiSection) : reasons;
  const aiReasons = aiSection >= 0 ? reasons.slice(aiSection) : [];
  const applicationStatus = decision.raw_ibv_data?.application?.status;

  return (
    <div
      className={`bg-gray-900/50 border rounded-xl p-4 hover:border-gray-600 overflow-y-auto max-h-[800px] ${
        isNewlyCompleted
          ? 'transition-opacity duration-1000 opacity-100 animate-none'
          : 'transition-all duration-300 opacity-0 animate-fadeIn'
      } ${decision.high_risk ? 'border-orange-500/50' : 'border-gray-700'}`}
      style={isNewlyCompleted ? {} : { animationDelay: `${index * 50}ms`, animationFillMode: 'forwards' }}
    >
                    {/* Compact Header with Metrics */}
                    <div className="bg-gradient-to-r from-gray-800/50 to-gray-900/30 border border-gray-700/50 rounded-lg p-2.5 mb-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          {/* Top Row: Name + Decision | Ref + Timestamp + Account Age */}
                          <div className="flex items-center justify-between gap-3 mb-0.5">
                            <div className="flex items-center gap-2">
                              <h4 className="font-bold text-base text-white truncate">{decision.customer_name}</h4>
                              {decision.decision && (
                                <>
                                  <span
                                    onClick={(e) => {
                                      if (decision.decision === 'REVIEW') {
                                        e.stopPropagation();
                                        handleReviewClick(decision);
                                      }
                                    }}
                                    className={`px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide ${
                                      decision.decision === 'APPROVED'
                                        ? 'bg-green-500/20 text-green-400 border border-green-500/40'
                                        : decision.decision.startsWith('DECLINE')
                                        ? 'bg-red-500/20 text-red-400 border border-red-500/40'
                                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40 cursor-pointer hover:bg-yellow-500/30 transition-colors'
                                    }`}>
                                    {decision.decision === 'APPROVED' ? '✓ APPROVED' : decision.decision.startsWith('DECLINE') ? '✗ DECLINED' : '⚠ REVIEW'}
                                  </span>
                                  <div className="flex items-center gap-1">
                                    {/* Show approve button if not already approved */}
                                    {decision.decision !== 'APPROVED' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedReviewDecision(decision);
                                          handleApproveDeclineReview(true);
                                        }}
                                        disabled={reviewUpdating}
                                        className={`${
                                          selectedReviewDecision?.id === decision.id
                                            ? 'bg-green-600/60 border-green-400 animate-pulse'
                                            : 'bg-green-600/20 border-green-500/40'
                                        } hover:bg-green-600/40 text-green-400 p-1.5 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transform border`}
                                        title={selectedReviewDecision?.id === decision.id ? "Click again to confirm" : "Approve"}
                                      >
                                        <CheckCircle className="w-4 h-4" />
                                      </button>
                                    )}
                                    {/* Show decline button if not already declined */}
                                    {!decision.decision.startsWith('DECLINE') && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setSelectedReviewDecision(decision);
                                          handleApproveDeclineReview(false);
                                        }}
                                        disabled={reviewUpdating}
                                        className={`${
                                          selectedReviewDecision?.id === decision.id
                                            ? 'bg-red-600/60 border-red-400 animate-pulse'
                                            : 'bg-red-600/20 border-red-500/40'
                                        } hover:bg-red-600/40 text-red-400 p-1.5 rounded-md transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-110 transform border`}
                                        title={selectedReviewDecision?.id === decision.id ? "Click again to confirm" : "Decline"}
                                      >
                                        <XCircle className="w-4 h-4" />
                                      </button>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <span className="font-mono text-blue-400">{decision.reference_id}</span>
                              <span className="text-gray-700">•</span>
                              <Clock className="w-3 h-3 text-gray-500" />
                              <span>{formatTimestamp(decision.timestamp)}</span>
                              {decision.account_age_days != null && decision.account_age_days > 0 && (
                                <>
                                  <span className="text-gray-700">•</span>
                                  <span className={decision.account_age_days >= 180 ? 'text-green-400' : 'text-yellow-400'}>
                                    {decision.account_age_days}d
                                  </span>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Personal Info Subtitle */}
                          <div className="flex items-center gap-2 text-xs text-gray-400 mb-1">
                            {(() => {
                              // Extract employer/income source from payroll transactions
                              const payrollTx = decision.data_extraction?.accounts?.flatMap((acc: any) => acc.transactions || [])
                                .filter((tx: any) => tx.metadata?.is_income && tx.amount > 500)
                                .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())[0];

                              if (payrollTx) {
                                const desc = payrollTx.description;
                                const descUpper = desc.toUpperCase();

                                // Check for government programs first
                                if (descUpper.includes('ONTARIO DISABILITY SUPPORT') || descUpper.includes('ODSP')) {
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-400">🏛️</span>
                                        <span className="text-orange-400 font-semibold">ODSP (Disability)</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }
                                if (descUpper.includes('CANADA PENSION') || descUpper.includes('CPP')) {
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-blue-400">🏛️</span>
                                        <span className="text-cyan-400 font-semibold">CPP (Pension)</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }
                                if (descUpper.includes('OLD AGE SECURITY') || descUpper.includes('OAS')) {
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-blue-400">🏛️</span>
                                        <span className="text-cyan-400 font-semibold">OAS (Pension)</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }
                                if (descUpper.includes('EMPLOYMENT INSURANCE') || descUpper.includes(' EI ')) {
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-yellow-400">🏛️</span>
                                        <span className="text-yellow-400 font-semibold">EI (Unemployed)</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }
                                if (descUpper.includes('WORKERS COMP') || descUpper.includes('WSIB')) {
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-orange-400">🏛️</span>
                                        <span className="text-orange-400 font-semibold">WSIB (Workers Comp)</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }

                                // Skip generic transaction types
                                if (descUpper.includes('ELECTRONIC FUNDS TRANSFER') ||
                                    descUpper.includes('DIRECT DEPOSIT') ||
                                    descUpper.includes('E-TRANSFER') ||
                                    descUpper.includes('INTERAC')) {
                                  return null;
                                }

                                // Extract employer name - find capitalized words that aren't common keywords
                                const words = desc.split(/\s+/).filter((w: string) => {
                                  const upper = w.toUpperCase();
                                  return w.length > 2 &&
                                         upper === w &&
                                         !['PAY', 'PAYROLL', 'SALARY', 'DEPOSIT', 'DD', 'INC', 'LTD', 'CORP', 'THE', 'AND', 'FOR', 'FROM'].includes(upper);
                                });

                                if (words.length > 0) {
                                  const employer = words.slice(0, 3).join(' ');
                                  return (
                                    <>
                                      <span className="flex items-center gap-1">
                                        <span className="text-blue-400">💼</span>
                                        <span className="text-white font-medium">Employer:</span>
                                        <span className="text-cyan-400 font-semibold">{employer}</span>
                                      </span>
                                      <span className="text-gray-700">•</span>
                                    </>
                                  );
                                }
                              }

                              // Fallback: N/A
                              return (
                                <>
                                  <span className="flex items-center gap-1">
                                    <span className="text-gray-600">💼</span>
                                    <span className="text-white font-medium">Employer:</span>
                                    <span className="text-gray-500">N/A</span>
                                  </span>
                                  <span className="text-gray-700">•</span>
                                </>
                              );
                            })()}
                            {decision.account_age_days && (
                              <>
                                <span className="flex items-center gap-1">
                                  <span className="text-white font-medium">Account Age:</span>
                                  <span className={decision.account_age_days >= 180 ? 'text-green-400 font-semibold' : 'text-yellow-400 font-semibold'}>
                                    {decision.account_age_days} days
                                  </span>
                                </span>
                              </>
                            )}
                            {decision.criteria?.hasJob && (
                              <>
                                <span className="text-gray-700">•</span>
                                <span className="text-green-400 font-medium">✓ Employed</span>
                              </>
                            )}
                          </div>

                          {/* Metrics Row */}
                          <div className="flex items-center gap-3 text-xs">
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-3 h-3 text-blue-400" />
                              <span className="text-white font-semibold">${formatMoney(decision.income_analysis?.monthly_income || decision.monthly_income_avg || 0)}</span>
                              <span className="text-gray-500">income</span>
                            </span>
                            <span className="text-gray-700">•</span>
                            <span className="flex items-center gap-1">
                              <AlertCircle className="w-3 h-3 text-orange-400" />
                              <span className="text-white font-semibold">{decision.fee_analysis?.nsf_count || decision.nsf_count || 0}</span>
                              <span className="text-gray-500">NSF</span>
                            </span>
                            <span className="text-gray-700">•</span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3 text-purple-400" />
                              <span className="text-white font-semibold">{decision.lender_analysis?.lenders?.length || decision.lender_count || 0}</span>
                              <span className="text-gray-500">lenders</span>
                            </span>
                            <span className="text-gray-700">•</span>
                            <span className="flex items-center gap-1">
                              <TrendingUp className="w-3 h-3 text-green-400" />
                              <span className="text-white font-semibold">${formatMoney(decision.loan_requested || 0)}</span>
                              <span className="text-gray-500">loan</span>
                            </span>
                          </div>
                        </div>

                        {/* Actions Dropdown */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenDropdownId(openDropdownId === decision.id ? null : decision.id);
                            }}
                            className="flex-shrink-0 p-2 bg-gray-700/50 border border-gray-600/50 hover:border-gray-500/60 hover:bg-gray-600/50 rounded-lg text-gray-400 hover:text-white transition-all duration-200 hover:scale-105 active:scale-95"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>

                          {openDropdownId === decision.id && (
                            <div className="absolute right-0 top-full mt-1 w-36 bg-gray-800 border border-gray-700 rounded-lg shadow-xl shadow-black/50 z-50 overflow-hidden">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  setSelectedTransactionDecision(decision);
                                  setTransactionModalOpen(true);
                                }}
                                className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-purple-500/10 transition-colors border-b border-gray-700/50"
                              >
                                <FileText className="w-3 h-3 text-purple-400" />
                                <div className="text-xs text-white">Transactions</div>
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  setSelectedReportDecision(decision);
                                  setReportModalOpen(true);
                                }}
                                className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-orange-500/10 transition-colors border-b border-gray-700/50"
                              >
                                <FileText className="w-3 h-3 text-orange-400" />
                                <div className="text-xs text-white">Report</div>
                              </button>

                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  setSelectedUnifiedRawDecision(decision);
                                  setUnifiedRawDataModalOpen(true);
                                }}
                                className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-blue-500/10 transition-colors border-b border-gray-700/50"
                              >
                                <FileText className="w-3 h-3 text-blue-400" />
                                <div className="text-xs text-white">Raw Data</div>
                              </button>

                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);

                                  addNotification({
                                    type: 'task_started',
                                    priority: 'high',
                                    title: '🔄 Reprocessing Started',
                                    message: `${decision.customer_name} - Running forensics + risk scoring...`,
                                    metadata: {
                                      decisionId: decision.id,
                                      customerName: decision.customer_name
                                    }
                                  });

                                  try {
                                    const response = await fetch(`/api/ibv/reprocess/${decision.id}`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({})
                                    });

                                    if (response.ok) {
                                      addNotification({
                                        type: 'info',
                                        priority: 'high',
                                        title: '✅ Reprocessing Complete',
                                        message: `${decision.customer_name} - Forensics + risk scoring updated`,
                                        metadata: {
                                          decisionId: decision.id,
                                          customerName: decision.customer_name
                                        }
                                      });
                                      fetchDecisions();
                                    } else {
                                      let errorMessage = 'Unknown error';
                                      try {
                                        const contentType = response.headers.get('content-type');
                                        if (contentType && contentType.includes('application/json')) {
                                          const error = await response.json();
                                          errorMessage = error.error || error.message || 'Unknown error';
                                        } else {
                                          const text = await response.text();
                                          errorMessage = `Server error (${response.status}): ${text.substring(0, 100)}`;
                                        }
                                      } catch (parseErr) {
                                        errorMessage = `Failed to parse error response (${response.status})`;
                                      }
                                      addNotification({
                                        type: 'error',
                                        priority: 'high',
                                        title: '❌ Reprocessing Failed',
                                        message: `${decision.customer_name} - ${errorMessage}`,
                                        metadata: {
                                          decisionId: decision.id,
                                          customerName: decision.customer_name
                                        }
                                      });
                                    }
                                  } catch (err) {
                                    addNotification({
                                      type: 'error',
                                      priority: 'high',
                                      title: '❌ Reprocessing Error',
                                      message: `${decision.customer_name} - ${err}`,
                                      metadata: {
                                        decisionId: decision.id,
                                        customerName: decision.customer_name
                                      }
                                    });
                                  }
                                }}
                                className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-green-500/10 transition-colors border-b border-gray-700/50"
                              >
                                <RefreshCw className="w-3 h-3 text-green-400" />
                                <div className="text-xs text-white">Reprocess</div>
                              </button>

                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  setOpenDropdownId(null);
                                  if (!confirm(`Delete decision for ${decision.customer_name}? This cannot be undone.`)) return;

                                  addNotification({
                                    type: 'task_started',
                                    priority: 'high',
                                    title: '🗑️ Deleting Decision',
                                    message: `${decision.customer_name} - Removing from database...`,
                                    metadata: {
                                      decisionId: decision.id,
                                      customerName: decision.customer_name
                                    }
                                  });

                                  try {
                                    const response = await fetch(`/api/ibv/decisions/${decision.id}`, {
                                      method: 'DELETE'
                                    });

                                    if (response.ok) {
                                      addNotification({
                                        type: 'info',
                                        priority: 'high',
                                        title: '✅ Decision Deleted',
                                        message: `${decision.customer_name} - Removed from database`,
                                        metadata: {
                                          decisionId: decision.id,
                                          customerName: decision.customer_name
                                        }
                                      });
                                      fetchDecisions();
                                    } else {
                                      let errorMsg = 'Failed to delete decision';
                                      try {
                                        const errorData = await response.json();
                                        errorMsg = `${errorData.error || 'Failed to delete decision'}${errorData.details ? ': ' + errorData.details : ''}`;
                                      } catch (e) {
                                        errorMsg = `HTTP ${response.status}: ${response.statusText}`;
                                      }
                                      addNotification({
                                        type: 'error',
                                        priority: 'high',
                                        title: '❌ Delete Failed',
                                        message: `${decision.customer_name} - ${errorMsg}`,
                                        metadata: {
                                          decisionId: decision.id,
                                          customerName: decision.customer_name
                                        }
                                      });
                                    }
                                  } catch (err) {
                                    addNotification({
                                      type: 'error',
                                      priority: 'high',
                                      title: '❌ Delete Error',
                                      message: `${decision.customer_name} - ${err}`,
                                      metadata: {
                                        decisionId: decision.id,
                                        customerName: decision.customer_name
                                      }
                                    });
                                  }
                                }}
                                className="w-full px-2 py-1.5 flex items-center gap-2 text-left hover:bg-red-500/10 transition-colors"
                              >
                                <Trash2 className="w-3 h-3 text-red-400" />
                                <div className="text-xs text-white">Delete</div>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* AI Summary - Full Width Below */}
                    <div className="mb-3">
                        {(decision.senior_assessment?.narrative_summary || decision.risk_assessment?.summary || decision.ai_summary || decision.llm_reasoning) ? (
                          <div
                            className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 cursor-pointer hover:border-blue-400/40 hover:bg-blue-500/15 transition-all duration-200 group"
                            onClick={() => {
                              setSelectedAiReasoning({
                                reasoning: decision.senior_assessment?.narrative_summary || decision.risk_assessment?.summary || decision.ai_full_analysis || decision.llm_reasoning!,
                                decision
                              });
                              setAiModalOpen(true);
                            }}
                          >
                            <div className="flex items-center gap-2 mb-2">
                              <div className="text-xs font-semibold text-blue-300 flex items-center gap-2">
                                🤖 AI Quick Summary
                                <span className="text-[10px] text-blue-400/60 group-hover:text-blue-300/80 transition-colors">
                                  Click for full analysis
                                </span>
                              </div>
                            </div>
                            <div className="text-xs text-blue-200/90 leading-relaxed">
                              {decision.senior_assessment?.narrative_summary || decision.risk_assessment?.summary || decision.ai_summary || decision.llm_reasoning?.split('\n').slice(0, 3).join(' ').substring(0, 300)}
                              {!decision.senior_assessment?.narrative_summary && !decision.risk_assessment?.summary && !decision.ai_summary && (decision.llm_reasoning?.length || 0) > 300 ? '...' : ''}
                            </div>
                          </div>
                        ) : decision.llm_pending ? (
                          <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg p-3 flex items-center justify-center gap-3">
                            <div className="relative">
                              <div className="w-6 h-6 border-2 border-purple-500/30 border-t-purple-400 rounded-full animate-spin"></div>
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[10px]">🤖</span>
                              </div>
                            </div>
                            <div className="text-xs text-purple-300">
                              <span className="font-medium">AI Analysis Generating</span>
                              <span className="text-purple-400/60 ml-1 animate-pulse">...</span>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg p-3 flex items-center justify-center text-gray-500 text-xs">
                            No AI commentary available
                          </div>
                        )}
                    </div>

                    {/* Analysis Results - Tab Navigation */}
                    {(() => {
                      const hasUncertainties = (decision.rust_result?.uncertainty_report?.items?.length || 0) > 0 ||
                                               (decision.uncertainty_resolution && !decision.uncertainty_resolution.skipped && (decision.uncertainty_resolution.resolutions?.length || 0) > 0);
                      const hasFees = decision.fee_analysis;
                      const hasIncome = decision.income_analysis;
                      const hasLenders = (decision.lender_analysis?.lenders?.length || 0) > 0;
                      const hasSpending = decision.spending_analysis;
                      const hasTrends = decision.trend_analysis;
                      const hasForensic = decision.forensic_analysis && (decision.forensic_analysis.gambling || decision.forensic_analysis.cash_patterns || decision.forensic_analysis.deposit_seizure);
                      const hasEtransfer = decision.etransfer_analysis;
                      const hasPaydayDrain = decision.payday_drain_analysis;

                      const tabs = [
                        hasUncertainties && { id: 'uncertainties', label: 'Uncertainties', icon: '⚠', color: 'amber' },
                        hasFees && { id: 'fees', label: 'Fees', icon: '💰', color: 'red' },
                        hasIncome && { id: 'income', label: 'Income', icon: '📈', color: 'green' },
                        hasLenders && { id: 'lenders', label: 'Lenders', icon: '🏦', color: 'purple' },
                        hasSpending && { id: 'spending', label: 'Spending', icon: '💳', color: 'blue' },
                        hasTrends && { id: 'trends', label: 'Trends', icon: '📊', color: 'indigo' },
                        hasForensic && { id: 'forensic', label: 'Forensic', icon: '🔍', color: 'orange' },
                        hasEtransfer && { id: 'etransfer', label: 'E-Transfer', icon: '↔️', color: 'cyan' },
                        hasPaydayDrain && { id: 'payday', label: 'Payday', icon: '💸', color: 'rose' },
                      ].filter(Boolean) as Array<{ id: string; label: string; icon: string; color: string }>;

                      if (tabs.length === 0) return null;

                      // Auto-select first tab if none selected
                      if (!activeTab && tabs.length > 0) {
                        setTimeout(() => setActiveTab(tabs[0].id), 0);
                      }

                      const colorMap: Record<string, string> = {
                        amber: 'border-amber-500 bg-amber-500/10 text-amber-400',
                        red: 'border-red-500 bg-red-500/10 text-red-400',
                        green: 'border-green-500 bg-green-500/10 text-green-400',
                        purple: 'border-purple-500 bg-purple-500/10 text-purple-400',
                        blue: 'border-blue-500 bg-blue-500/10 text-blue-400',
                        indigo: 'border-indigo-500 bg-indigo-500/10 text-indigo-400',
                        orange: 'border-orange-500 bg-orange-500/10 text-orange-400',
                        cyan: 'border-cyan-500 bg-cyan-500/10 text-cyan-400',
                        rose: 'border-rose-500 bg-rose-500/10 text-rose-400',
                      };

                      return (
                        <div className="bg-gray-800/30 border border-gray-700/50 rounded-lg overflow-hidden mb-3">
                          {/* Tab Navigation */}
                          <div className="flex gap-1 p-2 border-b border-gray-700/50 overflow-x-auto">
                            {tabs.map(tab => (
                              <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1 ${
                                  activeTab === tab.id
                                    ? colorMap[tab.color]
                                    : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/30'
                                }`}
                              >
                                <span>{tab.icon}</span>
                                <span>{tab.label}</span>
                              </button>
                            ))}
                          </div>

                          {/* Tab Content */}
                          <div className="p-3">
                            {/* Uncertainties Tab */}
                            {activeTab === 'uncertainties' && hasUncertainties && (
                              <UncertaintyPanel
                                uncertaintyReport={decision.rust_result?.uncertainty_report}
                                uncertaintyResolution={decision.uncertainty_resolution}
                                processingTime={{
                                  rust_ms: decision.rust_processing_time_ms,
                                  uncertainty_resolution_ms: decision.uncertainty_resolution?.processing_time_ms
                                }}
                              />
                            )}

                            {/* Fees Tab */}
                            {activeTab === 'fees' && hasFees && decision.fee_analysis && (
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between"><span className="text-gray-400">NSF Fees:</span><span className="text-white font-medium">${decision.fee_analysis.nsf_fees.toFixed(2)} ({decision.fee_analysis.nsf_count}x)</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Overdraft:</span><span className="text-white font-medium">${decision.fee_analysis.overdraft_fees.toFixed(2)} ({decision.fee_analysis.overdraft_count}x)</span></div>
                                <div className="flex justify-between pt-2 border-t border-gray-700/50"><span className="text-gray-400">Total Fees:</span><span className="text-red-400 font-bold">${decision.fee_analysis.total_fees.toFixed(2)}</span></div>
                              </div>
                            )}

                            {/* Income Tab */}
                            {activeTab === 'income' && hasIncome && decision.income_analysis && (
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between"><span className="text-gray-400">Monthly Income:</span><span className="text-green-400 font-bold">${decision.income_analysis.monthly_income.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Payroll:</span><span className="text-white">${decision.income_analysis.payroll_income.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Government:</span><span className="text-white">${decision.income_analysis.government_income.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Other:</span><span className="text-white">${decision.income_analysis.other_income.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Stability:</span><span className={decision.income_analysis.income_stability >= 0.7 ? 'text-green-400' : decision.income_analysis.income_stability >= 0.5 ? 'text-yellow-400' : 'text-red-400'}>{(decision.income_analysis.income_stability * 100).toFixed(0)}%</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Median:</span><span className="text-white">${decision.income_analysis.median_income.toFixed(2)}</span></div>
                                {decision.income_analysis.high_variance && (
                                  <div className="flex items-center gap-1 text-yellow-400 pt-2 border-t border-gray-700/50">
                                    <AlertTriangle className="w-3 h-3" />
                                    <span>High income variance detected</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Lenders Tab */}
                            {activeTab === 'lenders' && hasLenders && decision.lender_analysis && decision.lender_analysis.lenders && (
                              <div className="space-y-2 text-xs">
                                {decision.lender_analysis.total_payday_debt > 0 && (
                                  <div className="flex justify-between pb-2 border-b border-gray-700/50">
                                    <span className="text-gray-400">Total Debt:</span>
                                    <span className="text-red-400 font-bold">${decision.lender_analysis.total_payday_debt.toFixed(2)}</span>
                                  </div>
                                )}
                                {decision.lender_analysis.lenders.map((lender: any, idx: number) => (
                                  <div key={idx} className="flex items-center justify-between">
                                    <div className="flex items-center gap-1.5">
                                      <span className={`w-2 h-2 rounded-full ${lender.grade === 'A' ? 'bg-green-400' : lender.grade === 'B' ? 'bg-yellow-400' : lender.grade === 'C' ? 'bg-orange-400' : 'bg-red-400'}`}></span>
                                      <span className="text-gray-300">{lender.name.replace(/\s*\(Inverite Categorized\)/gi, '')}</span>
                                      <span className="text-gray-500 text-[10px]">({lender.grade})</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-gray-400">${lender.total_borrowed.toFixed(0)}</span>
                                      {lender.bounce_count > 0 && <span className="text-red-400">⚠{lender.bounce_count}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Spending Tab */}
                            {activeTab === 'spending' && hasSpending && decision.spending_analysis && (
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between"><span className="text-gray-400">Risky:</span><span className="text-red-400 font-medium">${(decision.spending_analysis.risky_spending || 0).toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Essential:</span><span className="text-green-400 font-medium">${(decision.spending_analysis.essential_spending || 0).toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Discretionary:</span><span className="text-white">${(decision.spending_analysis.discretionary_spending || 0).toFixed(2)}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">ATM Withdrawals:</span><span className="text-white">{decision.spending_analysis.atm_withdrawal_count || 0}x</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Credit Card Payments:</span><span className="text-white">${(decision.spending_analysis.credit_card_payments || 0).toFixed(2)}</span></div>
                                {decision.spending_analysis.alcohol_to_groceries_ratio > 0 && (
                                  <div className="flex justify-between pt-2 border-t border-gray-700/50">
                                    <span className="text-gray-400">Alcohol/Groceries Ratio:</span>
                                    <span className={decision.spending_analysis.alcohol_to_groceries_ratio > 0.3 ? 'text-red-400' : 'text-yellow-400'}>{(decision.spending_analysis.alcohol_to_groceries_ratio * 100).toFixed(1)}%</span>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Trends Tab */}
                            {activeTab === 'trends' && hasTrends && decision.trend_analysis && (
                              <div className="grid grid-cols-2 gap-3 text-xs">
                                <div className="space-y-1">
                                  <div className="text-gray-500 text-[10px] uppercase">Income Trend</div>
                                  <div className={`font-medium ${decision.trend_analysis.income_trend.includes('improving') ? 'text-green-400' : decision.trend_analysis.income_trend.includes('declining') ? 'text-red-400' : 'text-gray-300'}`}>
                                    {decision.trend_analysis.income_trend.includes('improving') ? '📈 Improving' : decision.trend_analysis.income_trend.includes('declining') ? '📉 Declining' : '➡️ Stable'}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-gray-500 text-[10px] uppercase">NSF Trend</div>
                                  <div className={`font-medium ${decision.trend_analysis.nsf_trend.includes('improving') ? 'text-green-400' : decision.trend_analysis.nsf_trend.includes('declining') || decision.trend_analysis.nsf_trend.includes('worsening') ? 'text-red-400' : 'text-gray-300'}`}>
                                    {decision.trend_analysis.nsf_trend.includes('improving') ? '📈 Improving' : decision.trend_analysis.nsf_trend.includes('declining') || decision.trend_analysis.nsf_trend.includes('worsening') ? '📉 Worsening' : '➡️ Stable'}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-gray-500 text-[10px] uppercase">Lender Trend</div>
                                  <div className={`font-medium ${decision.trend_analysis.lender_trend.includes('improving') ? 'text-green-400' : decision.trend_analysis.lender_trend.includes('declining') || decision.trend_analysis.lender_trend.includes('worsening') ? 'text-red-400' : 'text-gray-300'}`}>
                                    {decision.trend_analysis.lender_trend.includes('improving') ? '📈 Improving' : decision.trend_analysis.lender_trend.includes('declining') || decision.trend_analysis.lender_trend.includes('worsening') ? '📉 Worsening' : '➡️ Stable'}
                                  </div>
                                </div>
                                <div className="space-y-1">
                                  <div className="text-gray-500 text-[10px] uppercase">Balance Trend</div>
                                  <div className={`font-medium ${decision.trend_analysis.balance_trend.includes('improving') ? 'text-green-400' : decision.trend_analysis.balance_trend.includes('declining') ? 'text-red-400' : 'text-gray-300'}`}>
                                    {decision.trend_analysis.balance_trend.includes('improving') ? '📈 Improving' : decision.trend_analysis.balance_trend.includes('declining') ? '📉 Declining' : '➡️ Stable'}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Forensic Tab */}
                            {activeTab === 'forensic' && hasForensic && decision.forensic_analysis && (
                              <div className="space-y-3 text-xs">
                                {decision.forensic_analysis.gambling && (
                                  <div className="space-y-1.5">
                                    <div className="font-medium text-orange-400 flex items-center gap-1"><span>🎰</span> Gambling Analysis</div>
                                    <div className="flex justify-between"><span className="text-gray-400">Net Loss:</span><span className="text-red-400 font-bold">${decision.forensic_analysis.gambling.net_loss.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Total Wagered:</span><span className="text-white">${decision.forensic_analysis.gambling.total_wagered.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Total Won:</span><span className="text-white">${decision.forensic_analysis.gambling.total_won.toFixed(2)}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Frequency:</span><span className="text-white">{decision.forensic_analysis.gambling.frequency}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Merchants:</span><span className="text-white">{decision.forensic_analysis.gambling.merchant_count}</span></div>
                                  </div>
                                )}
                                {decision.forensic_analysis.cash_patterns && (
                                  <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                                    <div className="font-medium text-orange-400 flex items-center gap-1"><span>💵</span> Cash Patterns</div>
                                    <div className="flex justify-between"><span className="text-gray-400">ATM Count:</span><span className="text-white">{decision.forensic_analysis.cash_patterns.total_atm_count}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">ATM Total:</span><span className="text-white">${decision.forensic_analysis.cash_patterns.total_atm_amount?.toFixed(2) || '0.00'}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Pattern Detected:</span><span className={decision.forensic_analysis.cash_patterns.pattern_detected ? 'text-red-400' : 'text-green-400'}>{decision.forensic_analysis.cash_patterns.pattern_detected ? 'Yes' : 'No'}</span></div>
                                  </div>
                                )}
                                {decision.forensic_analysis.deposit_seizure && decision.forensic_analysis.deposit_seizure.pattern_count > 0 && (
                                  <div className="space-y-1.5 pt-2 border-t border-gray-700/50">
                                    <div className="font-medium text-red-400 flex items-center gap-1"><span>⚠️</span> Deposit Seizure</div>
                                    <div className="flex justify-between"><span className="text-gray-400">Patterns Found:</span><span className="text-red-400 font-bold">{decision.forensic_analysis.deposit_seizure.pattern_count}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Total Seized:</span><span className="text-red-400 font-bold">${decision.forensic_analysis.deposit_seizure.total_seized?.toFixed(2) || '0.00'}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Burned Account:</span><span className={decision.forensic_analysis.deposit_seizure.burned_account ? 'text-red-400' : 'text-green-400'}>{decision.forensic_analysis.deposit_seizure.burned_account ? 'Yes' : 'No'}</span></div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* E-Transfer Tab */}
                            {activeTab === 'etransfer' && hasEtransfer && decision.etransfer_analysis && (
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between"><span className="text-gray-400">Total E-Transfers:</span><span className="text-white font-medium">{decision.etransfer_analysis.total_etransfers}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Outbound:</span><span className="text-red-400">{decision.etransfer_analysis.etransfers_out}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Inbound:</span><span className="text-green-400">{decision.etransfer_analysis.etransfers_in}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Unique Recipients:</span><span className="text-white">{decision.etransfer_analysis.unique_recipients}</span></div>
                                {(decision.etransfer_analysis.hidden_lender_count || 0) > 0 && (
                                  <div className="pt-2 border-t border-gray-700/50 space-y-1">
                                    <div className="flex justify-between"><span className="text-gray-400">Hidden Lenders:</span><span className="text-red-400 font-bold">{decision.etransfer_analysis.hidden_lender_count}</span></div>
                                    <div className="flex justify-between"><span className="text-gray-400">Hidden Lender Total:</span><span className="text-red-400 font-bold">${decision.etransfer_analysis.hidden_lender_total?.toFixed(2) || '0'}</span></div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Payday Drain Tab */}
                            {activeTab === 'payday' && hasPaydayDrain && decision.payday_drain_analysis && (
                              <div className="space-y-2 text-xs">
                                <div className="flex justify-between"><span className="text-gray-400">Paydays Analyzed:</span><span className="text-white font-medium">{decision.payday_drain_analysis.paydays_analyzed}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Avg Drain %:</span><span className={decision.payday_drain_analysis.avg_drain_pct > 80 ? 'text-red-400 font-bold' : 'text-yellow-400'}>{decision.payday_drain_analysis.avg_drain_pct.toFixed(1)}%</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Over 100% Count:</span><span className="text-red-400 font-bold">{decision.payday_drain_analysis.over_100pct_count}</span></div>
                                <div className="flex justify-between"><span className="text-gray-400">Debt Spiral Score:</span><span className={decision.payday_drain_analysis.debt_spiral_score > 7 ? 'text-red-400' : decision.payday_drain_analysis.debt_spiral_score > 4 ? 'text-yellow-400' : 'text-green-400'}>{decision.payday_drain_analysis.debt_spiral_score}/10</span></div>
                                <div className="pt-2 border-t border-gray-700/50">
                                  <div className="text-gray-400 mb-1">Pattern:</div>
                                  <div className="text-white text-[11px]">{decision.payday_drain_analysis.pattern}</div>
                                </div>
                              </div>
                            )}

                          </div>
                        </div>
                      );
                    })()}


                      {/* Criteria Badges Card */}
                      {decision.criteria && (
                        <div className="bg-gradient-to-br from-indigo-500/10 to-indigo-600/5 border border-indigo-500/20 rounded-lg p-4 mb-3">
                          <div className="flex items-center gap-2 mb-3">
                            <svg className="w-4 h-4 text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h5 className="text-sm font-semibold text-indigo-300">Decision Criteria</h5>
                            <span className="text-xs text-gray-500 ml-auto">
                              {Object.values(decision.criteria).filter(v => v === true).length}/6 passed
                            </span>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {/* Job/Income */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.hasJob
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.hasJob ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.hasJob ? 'text-green-300' : 'text-red-300'}`}>
                                💼 Job
                              </span>
                            </div>

                            {/* NSF Rate */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.lowNSFRate
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.lowNSFRate ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.lowNSFRate ? 'text-green-300' : 'text-red-300'}`}>
                                📊 NSF
                              </span>
                            </div>

                            {/* Repayment */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.paysLendersBack
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.paysLendersBack ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.paysLendersBack ? 'text-green-300' : 'text-red-300'}`}>
                                💸 Repay
                              </span>
                            </div>

                            {/* Fees */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.reasonableFees
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.reasonableFees ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.reasonableFees ? 'text-green-300' : 'text-red-300'}`}>
                                💰 Fees
                              </span>
                            </div>

                            {/* Affordable */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.loanAffordable
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.loanAffordable ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.loanAffordable ? 'text-green-300' : 'text-red-300'}`}>
                                ✅ Afford
                              </span>
                            </div>

                            {/* Leverage */}
                            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${
                              decision.criteria.notOverleveraged
                                ? 'bg-green-500/10 border border-green-500/30'
                                : 'bg-red-500/10 border border-red-500/30'
                            }`}>
                              {decision.criteria.notOverleveraged ? (
                                <svg className="w-4 h-4 text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              )}
                              <span className={`text-xs font-medium ${decision.criteria.notOverleveraged ? 'text-green-300' : 'text-red-300'}`}>
                                ⚖️ Leverage
                              </span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Burn Rate & E-Transfer Activity Card */}
                      {(() => {
                        // Get all transactions from raw_ibv_data
                        const accountsData = decision.raw_ibv_data?.accounts
                          || decision.raw_ibv_data?.ibv_data?.accounts
                          || decision.raw_ibv_data?.raw_ibv_data?.accounts
                          || decision.raw_ibv_data?.raw_ibv_data?.ibv_data?.accounts;

                        if (!accountsData || !Array.isArray(accountsData)) return null;

                        const allTransactions = accountsData.flatMap((account: any) =>
                          (account.transactions || []).map((txn: any) => ({
                            date: txn.date,
                            description: (txn.description || '').toLowerCase(),
                            amount: txn.amount || 0,
                            balance: txn.balance || 0,
                            type: txn.type || txn.tx_type
                          }))
                        ).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

                        if (allTransactions.length === 0) return null;

                        // Find most recent income deposit (paycheck)
                        const incomeKeywords = ['payroll', 'salary', 'wages', 'direct deposit', 'dd deposit', 'employment', 'employer'];
                        const recentPaycheck = allTransactions.find(txn =>
                          txn.type === 'credit' && txn.amount > 500 &&
                          incomeKeywords.some(keyword => txn.description.includes(keyword))
                        );

                        let daysToTwentyPercent = null;
                        if (recentPaycheck) {
                          const paycheckDate = new Date(recentPaycheck.date);
                          const twentyPercentThreshold = recentPaycheck.amount * 0.2;

                          // Find transactions after this paycheck
                          const afterPaycheck = allTransactions.filter(txn =>
                            new Date(txn.date) >= paycheckDate
                          ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                          // Find when balance first drops below 20%
                          const hitThreshold = afterPaycheck.find(txn => txn.balance < twentyPercentThreshold);
                          if (hitThreshold) {
                            const thresholdDate = new Date(hitThreshold.date);
                            daysToTwentyPercent = Math.floor((thresholdDate.getTime() - paycheckDate.getTime()) / (1000 * 60 * 60 * 24));
                          }
                        }

                        // Calculate e-transfer activity for most recent pay cycle
                        const etransferKeywords = ['e-transfer', 'etransfer', 'interac e-transfer', 'interac', 'emt', 'sent money', 'request money'];
                        let etransferIn = { count: 0, total: 0 };
                        let etransferOut = { count: 0, total: 0 };

                        if (recentPaycheck) {
                          const paycheckDate = new Date(recentPaycheck.date);
                          const cycleTransactions = allTransactions.filter(txn => {
                            const txnDate = new Date(txn.date);
                            return txnDate >= paycheckDate && txnDate < new Date(paycheckDate.getTime() + (14 * 24 * 60 * 60 * 1000)); // 14 days
                          });

                          cycleTransactions.forEach(txn => {
                            if (etransferKeywords.some(keyword => txn.description.includes(keyword))) {
                              if (txn.type === 'credit') {
                                etransferIn.count++;
                                etransferIn.total += Math.abs(txn.amount);
                              } else if (txn.type === 'debit') {
                                etransferOut.count++;
                                etransferOut.total += Math.abs(txn.amount);
                              }
                            }
                          });
                        }

                        if (daysToTwentyPercent === null && etransferIn.count === 0 && etransferOut.count === 0) return null;

                        return (
                          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/5 border border-purple-500/20 rounded-lg p-4 mb-3">
                            <div className="flex items-center gap-2 mb-3">
                              <svg className="w-4 h-4 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <h5 className="text-sm font-semibold text-purple-300">🔥 Burn Rate & E-Transfers</h5>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              {/* Days to 20% */}
                              <div className="bg-gray-900/50 rounded-lg p-3">
                                <div className={`text-2xl font-bold mb-1 ${
                                  daysToTwentyPercent === null ? 'text-gray-500' :
                                  daysToTwentyPercent <= 3 ? 'text-red-400' :
                                  daysToTwentyPercent <= 7 ? 'text-orange-400' :
                                  'text-green-400'
                                }`}>
                                  {daysToTwentyPercent !== null ? `${daysToTwentyPercent}d` : 'N/A'}
                                </div>
                                <div className="text-[10px] text-gray-400 leading-tight">Days to &lt;20% left</div>
                              </div>

                              {/* E-Transfer IN */}
                              <div className="bg-gray-900/50 rounded-lg p-3">
                                <div className="text-lg font-bold text-green-400 mb-1">
                                  {etransferIn.count} / ${etransferIn.total.toFixed(0)}
                                </div>
                                <div className="text-[10px] text-gray-400 leading-tight">E-Transfer IN</div>
                              </div>

                              {/* E-Transfer OUT */}
                              <div className="bg-gray-900/50 rounded-lg p-3">
                                <div className="text-lg font-bold text-red-400 mb-1">
                                  {etransferOut.count} / ${etransferOut.total.toFixed(0)}
                                </div>
                                <div className="text-[10px] text-gray-400 leading-tight">E-Transfer OUT</div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {/* LightningFast Footer */}
                      <div className="mt-4 pt-3 border-t border-gray-800/50">
                        <p className="text-[10px] text-gray-600 text-center">
                          processed by LightningFast advanced I/O & data processing solutions
                        </p>
                      </div>
    </div>
  );
};

export default DecisionCard;
